/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useState } from 'react';
import { Form, Skeleton, Alert, FormInstance, Checkbox } from 'antd';
import localforage from 'localforage';
import { useAppDispatch } from '@/hooks/useRedux';
import { fetchIdentifications } from '@/redux/auth/registration/thunk';
import { extractErrorMessage } from '@/utils/errorHandling';

interface IdentificationField {
  id: string;
  name: string;
  country: string;
  is_required: boolean;
  is_primary: boolean;
}

interface Step2ContentProps {
  form: FormInstance;
  sessionId?: string;
  errorProp?: any;
}

const labelClass =
  'block text-[15px] sm:text-[16px] font-semibold text-[#111827] mb-2';

const inputBase =
  'w-full h-[52px] sm:h-[56px] rounded-xl border bg-white px-4 sm:px-5 ' +
  'text-[16px] sm:text-[17px] text-[#111827] placeholder:text-[#9CA3AF] ' +
  'outline-none transition';

const focusGreen =
  'focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/20';
const borderDefault = 'border-[#D1D5DB]';

const Required = () => <span className="ml-1 text-[#DC2626]">*</span>;

function fieldErrorText(err: any) {
  if (!err) return null;
  if (Array.isArray(err)) return err.join(' ');
  if (typeof err === 'string') return err;
  return String(err);
}

export default function Step2Content({
  // form,
  sessionId,
  errorProp,
}: Step2ContentProps) {
  const dispatch = useAppDispatch();
  const [fields, setFields] = useState<IdentificationField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  useEffect(() => {
    const loadSessionAndFields = async () => {
      try {
        const sid =
          sessionId ??
          (await localforage.getItem<any>('signup.session'))?.session_id;

        if (!sid)
          throw new Error('Missing session_id for fetching identifications');

        const res = await dispatch(fetchIdentifications(sid)).unwrap();
        setFields(res || []);
      } catch (err: unknown) {
        setError(extractErrorMessage(err, 'Failed to load identification fields.'));
      } finally {
        setLoading(false);
      }
    };

    loadSessionAndFields();
  }, [dispatch, sessionId]);

  if (loading) return <Skeleton active />;
  if (error) return <Alert type="error" message={error} showIcon />;

  const requiredFields = fields.filter((f) => f.is_required);
  const optionalFields = fields.filter((f) => !f.is_required);

  // const bannerMsg =
  //   typeof errorProp === 'string'
  //     ? errorProp
  //     : errorProp?.detail ||
  //       (errorProp ? 'Please fix the highlighted errors below.' : null);

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* {bannerMsg ? (
        <Alert type="error" message={bannerMsg} showIcon className="!mb-0" />
      ) : null} */}

      {/* Last name (FIXED) */}
      <div>
        <label className={labelClass}>
          Last Name
          <Required />
        </label>

        <Form.Item
          name="last_name"
          className="mb-0"
          rules={[{ required: true, message: 'Last Name is required' }]}
        >
          <input
            className={`${inputBase} ${borderDefault} ${focusGreen}`}
            placeholder="e.g. Kamau"
            autoComplete="family-name"
          />
        </Form.Item>

        {errorProp?.fields?.last_name ? (
          <div className="mt-2 text-[13px] sm:text-[14px] text-[#DC2626]">
            {fieldErrorText(errorProp.fields.last_name)}
          </div>
        ) : null}
      </div>

      {/* Required ID fields (FIXED) */}
      {requiredFields.map((field) => {
        const fErr = errorProp?.fields?.[field.id];

        return (
          <div key={field.id}>
            <label className={labelClass}>
              {field.name}
              <Required />
            </label>

            <Form.Item
              name={field.id} // backend UUID id
              className="mb-0"
              rules={[{ required: true, message: `${field.name} is required` }]}
            >
              <input
                className={`${inputBase} ${borderDefault} ${focusGreen}`}
                placeholder={`Enter ${field.name}`}
                autoComplete="off"
                inputMode="text"
              />
            </Form.Item>

            {fErr ? (
              <div className="mt-2 text-[13px] sm:text-[14px] text-[#DC2626]">
                {fieldErrorText(fErr)}
              </div>
            ) : null}
          </div>
        );
      })}

      {/* Optional toggle */}
      {optionalFields.length > 0 ? (
        <div className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3">
          <Checkbox
            checked={showOptionalFields}
            onChange={(e) => setShowOptionalFields(e.target.checked)}
          >
            <span className="text-sm text-[#374151]">
              Add additional identification documents
            </span>
          </Checkbox>
        </div>
      ) : null}

      {/* Optional fields (FIXED) */}
      {showOptionalFields
        ? optionalFields.map((field) => {
            const fErr = errorProp?.fields?.[field.id];

            return (
              <div key={field.id}>
                <label className={labelClass}>{field.name}</label>

                <Form.Item name={field.id} className="mb-0">
                  <input
                    className={`${inputBase} ${borderDefault} ${focusGreen}`}
                    placeholder={`Enter ${field.name}`}
                    autoComplete="off"
                    inputMode="text"
                  />
                </Form.Item>

                {fErr ? (
                  <div className="mt-2 text-[13px] sm:text-[14px] text-[#DC2626]">
                    {fieldErrorText(fErr)}
                  </div>
                ) : null}
              </div>
            );
          })
        : null}
    </div>
  );
}
